import React, { useState, useRef, useEffect } from "react";
import { useSelector } from "react-redux";
import toast from "react-hot-toast";
import {
  addNewDoc,
  setDocData,
  storage,
  getAllDocsSnap,
} from "../../../utils/firebase/Firebase.utils";
import { ref, uploadBytesResumable, getDownloadURL } from "firebase/storage";
import Label from "../Components/Label";
import SelectInput from "../Components/SelectInput";
import InputBox from "../Components/InputBox";
import { FiImage, FiUpload, FiPlus, FiTrash2 } from "react-icons/fi";

export default function AddContactForm({
  data,
  setAddMode,
  setEditMode,
  editMode,
  reportsTo,
  defaultCategory,
}) {
  const currentUser = useSelector((state) => state.userObject);
  const [formData, setFormData] = useState(
    editMode
      ? data
      : {
          reportsTo: reportsTo || null,
          category: defaultCategory || undefined,
          additionalContacts: [],
        }
  );
  const [submitting, setSubmitting] = useState(false);
  const [photoProgress, setPhotoProgress] = useState(0);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [contacts, setContacts] = useState([]);
  const fileInputRef = useRef(null);
  const [newContact, setNewContact] = useState({ type: "mobile", value: "" });

  useEffect(() => {
    const fetchContacts = async () => {
      await getAllDocsSnap("contacts", (data) => {
        setContacts(data);
      });
    };
    fetchContacts();
  }, []);

  useEffect(() => {
    // Ensure additionalContacts exists in formData
    if (!formData.additionalContacts) {
      setFormData((prev) => ({ ...prev, additionalContacts: [] }));
    }
  }, [formData]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleAddContact = () => {
    if (!newContact.value.trim()) {
      toast.error("Contact detail cannot be empty");
      return;
    }

    setFormData({
      ...formData,
      additionalContacts: [
        ...(formData.additionalContacts || []),
        { ...newContact, id: Date.now() },
      ],
    });
    setNewContact({ type: "mobile", value: "" });
  };

  const handleRemoveContact = (id) => {
    setFormData({
      ...formData,
      additionalContacts: formData.additionalContacts.filter(
        (contact) => contact.id !== id
      ),
    });
  };

  const handleNewContactChange = (e) => {
    const { name, value } = e.target;
    setNewContact({ ...newContact, [name]: value });
  };

  const handlePhotoUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    const validTypes = ["image/jpeg", "image/png", "image/gif", "image/webp"];
    if (!validTypes.includes(file.type)) {
      toast.error("Please upload an image file (JPEG, PNG, GIF, or WEBP)");
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error("File size should be less than 5MB");
      return;
    }

    setUploadingPhoto(true);
    const storageRef = ref(
      storage,
      `contacts/${Date.now()}_${file.name.replace(/[^a-zA-Z0-9.]/g, "_")}`
    );
    const uploadTask = uploadBytesResumable(storageRef, file);

    uploadTask.on(
      "state_changed",
      (snapshot) => {
        const progress =
          (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
        setPhotoProgress(progress);
      },
      (error) => {
        toast.error("Upload failed. Please try again.");
        setUploadingPhoto(false);
        console.error("Upload error:", error);
      },
      () => {
        getDownloadURL(uploadTask.snapshot.ref).then((downloadURL) => {
          setFormData((prev) => ({ ...prev, photoUrl: downloadURL }));
          toast.success("Photo uploaded successfully!");
          setUploadingPhoto(false);
          setPhotoProgress(0);
        });
      }
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      if (editMode) {
        const docObject = {
          ...formData,
          updatedBy: currentUser.email,
          updatedAt: new Date().toLocaleString(),
        };
        await setDocData("contacts", data.id, docObject);
        toast.success("Contact Updated Successfully");
        setEditMode(false);
      } else {
        const docObject = {
          ...formData,
          createdBy: currentUser.email,
          user: currentUser.uid,
          createdAt: new Date().toLocaleString(),
        };
        await addNewDoc("contacts", docObject);
        toast.success("Contact Added Successfully");
        setAddMode(false);
      }
    } catch (error) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const categoryOptions = [
    {
      label: "Condo Corp Directory",
      value: "Condo Corp Directory",
    },
    {
      label: "Emergency Hotlines",
      value: "Emergency Hotlines",
    },
  ];

  // Filter contacts to use as manager options (exclude the current contact if in edit mode)
  const managerOptions = contacts
    .filter((contact) => !editMode || contact.id !== data?.id)
    .map((contact) => ({
      label: `${contact.name} (${contact.title})`,
      value: contact.id,
    }));

  const contactTypeOptions = [
    { label: "Mobile", value: "mobile" },
    { label: "Landline", value: "landline" },
    { label: "Email", value: "email" },
    { label: "Hotline", value: "hotline" },
    { label: "Other", value: "other" },
  ];

  return (
    <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl relative">
      <button
        className="absolute top-4 right-4 text-gray-500 hover:text-gray-700"
        onClick={() => {
          setAddMode(false);
          setEditMode(false);
        }}
      >
        <svg
          className="w-6 h-6"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M6 18L18 6M6 6l12 12"
          />
        </svg>
      </button>

      <div className="p-6">
        <h2 className="text-2xl font-semibold text-gray-800 mb-6">
          {editMode ? "Edit Contact" : "Add New Contact"}
        </h2>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Photo Upload Section */}
          <div className="flex justify-center mb-2">
            <div className="w-32 h-32 relative">
              <div
                className="w-full h-full rounded-full border-2 border-dashed border-gray-300 flex flex-col items-center justify-center cursor-pointer overflow-hidden bg-gray-50"
                onClick={() => fileInputRef.current.click()}
              >
                {formData.photoUrl ? (
                  <img
                    src={formData.photoUrl}
                    alt="Contact"
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <>
                    <FiImage className="w-8 h-8 text-gray-400 mb-2" />
                    <span className="text-xs text-gray-500 text-center">
                      Upload Photo
                    </span>
                  </>
                )}
                {uploadingPhoto && (
                  <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center">
                    <div className="text-white text-xs">
                      {Math.round(photoProgress)}%
                    </div>
                  </div>
                )}
              </div>
              <input
                type="file"
                ref={fileInputRef}
                onChange={handlePhotoUpload}
                className="hidden"
                accept="image/*"
              />
              {formData.photoUrl && (
                <div className="absolute bottom-0 right-0">
                  <button
                    type="button"
                    className="bg-blue-600 rounded-full p-1.5 text-white shadow-md"
                    onClick={() => fileInputRef.current.click()}
                  >
                    <FiUpload className="w-3.5 h-3.5" />
                  </button>
                </div>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <SelectInput
                name="category"
                label="Category"
                value={categoryOptions.find(
                  (option) => option.value === formData.category
                )}
                options={categoryOptions}
                onChange={(value) => {
                  setFormData({ ...formData, category: value.value });
                }}
                required
              />
            </div>

            <div>
              <Label label="Name" />
              <InputBox
                name="name"
                type="text"
                onChange={handleChange}
                placeholder="Enter name of Contact"
                value={formData.name}
                disabled={submitting}
                required
              />
            </div>

            <div>
              <Label label="Title / Position" />
              <InputBox
                name="title"
                type="text"
                onChange={handleChange}
                placeholder="Enter Title"
                required
                disabled={submitting}
                value={formData.title}
              />
            </div>

            <div>
              <Label label="Department" />
              <InputBox
                name="department"
                type="text"
                onChange={handleChange}
                placeholder="Enter Department"
                required
                disabled={submitting}
                value={formData.department}
              />
            </div>

            <div>
              <Label label="Contact Number" />
              <InputBox
                name="contactNumber"
                type="tel"
                onChange={handleChange}
                placeholder="Enter Contact Number"
                required
                disabled={submitting}
                value={formData.contactNumber}
              />
            </div>

            <div>
              <Label label="Landline Number" />
              <InputBox
                name="landline"
                type="tel"
                onChange={handleChange}
                disabled={submitting}
                placeholder="Enter Landline Number"
                value={formData.landline}
              />
            </div>

            <div className="md:col-span-2">
              <Label label="Email Address" />
              <InputBox
                name="email"
                type="email"
                onChange={handleChange}
                disabled={submitting}
                placeholder="Enter Email Address"
                value={formData.email}
              />
            </div>

            <div className="md:col-span-2">
              <Label label="Reports To" />
              <SelectInput
                name="reportsTo"
                placeholder="Select Manager"
                isClearable
                value={managerOptions.find(
                  (option) => option.value === formData.reportsTo
                )}
                options={managerOptions}
                onChange={(value) => {
                  setFormData({ ...formData, reportsTo: value?.value || null });
                }}
                noOptionsMessage={() =>
                  "No contacts available to select as manager"
                }
              />
              <p className="text-xs text-gray-500 mt-1">
                Optional. Select a manager to build the organizational chart.
              </p>
            </div>

            {/* Additional Contacts Section */}
            <div className="md:col-span-2">
              <div className="border rounded-lg p-4 mb-4">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="font-medium text-gray-700">
                    Additional Contact Details
                  </h3>
                </div>

                {formData.additionalContacts &&
                  formData.additionalContacts.length > 0 && (
                    <div className="space-y-3 mb-4">
                      {formData.additionalContacts.map((contact) => (
                        <div
                          key={contact.id}
                          className="flex items-center bg-gray-50 p-3 rounded-md"
                        >
                          <div className="flex-1">
                            <span className="text-sm font-medium text-gray-500 mr-2">
                              {contactTypeOptions.find(
                                (option) => option.value === contact.type
                              )?.label || contact.type}
                              :
                            </span>
                            <span className="text-gray-800">
                              {contact.value}
                            </span>
                          </div>
                          <button
                            type="button"
                            onClick={() => handleRemoveContact(contact.id)}
                            className="text-red-500 hover:text-red-700"
                          >
                            <FiTrash2 />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}

                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <div className="md:col-span-1">
                    <SelectInput
                      name="type"
                      label="Type"
                      placeholder="Contact Type"
                      value={contactTypeOptions.find(
                        (option) => option.value === newContact.type
                      )}
                      options={contactTypeOptions}
                      onChange={(value) => {
                        setNewContact({ ...newContact, type: value.value });
                      }}
                    />
                  </div>
                  <div className="md:col-span-2 flex space-x-2">
                    <InputBox
                      label="Detail"
                      name="value"
                      type="text"
                      onChange={handleNewContactChange}
                      placeholder="Enter contact detail"
                      value={newContact.value}
                      className="flex-grow"
                    />
                    <button
                      type="button"
                      onClick={handleAddContact}
                      className="px-3 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center"
                    >
                      <FiPlus className="mr-1" /> Add
                    </button>
                  </div>
                </div>

                <p className="text-xs text-gray-500 mt-2">
                  Add multiple contact methods like additional phones, emails,
                  or hotlines.
                </p>
              </div>
            </div>
          </div>

          <div className="flex justify-end space-x-4 mt-8">
            <button
              type="button"
              onClick={() => {
                setAddMode(false);
                setEditMode(false);
              }}
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors"
              disabled={submitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting || uploadingPhoto}
              className="px-4 py-2 text-white bg-blue-600 rounded-md hover:bg-blue-700 transition-colors flex items-center"
            >
              {submitting ? (
                <>
                  <svg
                    className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  Processing...
                </>
              ) : (
                "Submit"
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
